import { IChartContainer } from "../models/base/chart-container.interface";
import { SmartStopLinesModel } from "../models/smart-risk-level/smart-stop-lines.model";
import { IComponent } from "./component.interface";

export class DrawSmartRiskComponent implements IComponent {
    public id = '';

    private base: IChartContainer;
    private element: HTMLElement;
    private model = new SmartStopLinesModel();
    private shouldShowGredientColors = false;
    private gredientTotalHeight = 0;

    constructor(element: HTMLElement, base: IChartContainer) {
        this.id = 'smart-risk';
        this.base = base;
        this.element = element;
        this.base.tiChartSeries?.subscribeExtremeChange(() => {
            this.redraw();
        });
        this.base.tiChartSeries?.subscribeRedrawChange(() => {
            this.redraw();
        });
        this.subscribeSmartRisk();
    }

    private redrawTimer: any;
    private redraw() {
        clearTimeout(this.redrawTimer);
        this.redrawTimer = setTimeout(() => {
            this.draw();
            this.setYAxisLabelPosition();
            this.toggleGredientColors();
        }, 200);
    }

    public remove() {
        this.removeStyle();
        this.element.findFirst('.smart-stop')?.addClass('hidden');
        this.element.findFirst('.smart-target')?.addClass('hidden');
        this.element.findFirst('.stop-price')?.addClass('hidden');
        this.element.findFirst('.target-price')?.addClass('hidden');
        this.element.findFirst('.smart-risk-gredient')?.addClass('hidden');
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return;
        }

        yAxis.removePlotLine(this.id + '-stop');
        yAxis.removePlotLine(this.id + '-target');
    }

    private removeStyle() {
        const highChartStyle = this.base.tiChartSeries?.element?.findFirst('.style-' + this.id);
        if (highChartStyle) {
            highChartStyle.remove();
        }
    }

    private createSytle() {
        this.removeStyle();
        const className = this.id;
        let styleTag = `<style>`;

        styleTag += `.highcharts-plot-line.${className}-stop {stroke: ${this.model.smartStopColor.toRGBA(0.5)}; stroke-dasharray: 3, 2;}`;
        styleTag += `.highcharts-plot-line.${className}-target {stroke: ${this.model.smartTargetColor.toRGBA(0.5)}; stroke-dasharray: 3, 2;}`;

        styleTag += `</style>`;

        const chartStyleContainer = `<div class="style-${className}">${styleTag}</div>`;
        this.base.tiChartSeries?.element.insertAdjacentHTML('afterbegin', chartStyleContainer);
    }

    private subscribeSmartRisk() {
        this.base.chartService.subscribeSmartRiskData((model: SmartStopLinesModel) => {
            this.model = model;
            this.draw();
            this.setYAxisLabelPosition();
            this.toggleGredientColors();
        });
    }

    private draw() {
        this.remove();
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis || !this.model.show) {
            return;
        }

        this.createSytle();
        yAxis.addPlotLine({
            value: this.model.smartStopLevel,
            className: this.id + '-stop',
            width: 1,
            id: this.id + '-stop',
            zIndex: 10
        });

        yAxis.addPlotLine({
            value: this.model.smartTargetLevel,
            className: this.id + '-target',
            width: 1,
            id: this.id + '-target',
            zIndex: 10
        });
    }

    private toggleGredientColors() {
        if (!this.model.show) {
            return;
        }

        const targetPriceElement = this.element.findFirst('.target-price');
        const stopPriceElement = this.element.findFirst('.stop-price');
        const smartRiskGredient = this.element.findFirst('.smart-risk-gredient');

        if (this.shouldShowGredientColors && this.base.tiChartSeries?.isCurrentBarVisible()) {
            smartRiskGredient?.removeClass('hidden');

            if (this.gredientTotalHeight - 32 < 20) {
                stopPriceElement?.addClass('hidden');
                targetPriceElement?.addClass('hidden');
            } else {
                stopPriceElement?.removeClass('hidden');
                targetPriceElement?.removeClass('hidden');
            }
        } else {
            smartRiskGredient?.addClass('hidden');
            stopPriceElement?.addClass('hidden');
            targetPriceElement?.addClass('hidden');
        }
    }

    private setYAxisLabelPosition() {
        const yAxis = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis || !this.model.show) {
            return;
        }

        this.shouldShowGredientColors = false;

        const extraTop = this.base.tiChartSeries?.hasAnyComparisonSymbols() ? 20 : 0;

        const min = yAxis.min ?? 0;
        const max = yAxis.max ?? 0;
        const smartStopElement = this.element.findFirst('.smart-stop');
        const stopPriceElement = this.element.findFirst('.stop-price');
        const smartStopPlotLine = this.base.tiChartSeries.element.findFirst('.highcharts-plot-line.smart-risk-stop');
        let stopPriceTop = 0;
        if (smartStopElement) {
            let top = 0;
            let shouldHideSmartStop = !smartStopPlotLine || !smartStopPlotLine.getAttribute('d');
            if (this.model.smartStopLevel < min) {
                top = yAxis.toPixels(min, true) + 30 + extraTop;
                shouldHideSmartStop = true;
            } else if (this.model.smartStopLevel > max) {
                top = yAxis.toPixels(max, true) + 30;
                shouldHideSmartStop = true;
            } else {
                top = yAxis.toPixels(this.model.smartStopLevel, true) + (this.model.isLong ? 30 + extraTop : 42 + extraTop);
            }

            if (shouldHideSmartStop) {
                smartStopElement.addClass('hidden');
            } else {
                smartStopElement.removeClass('hidden');
            }

            stopPriceTop = top;
            smartStopElement.style.top = stopPriceTop + 'px';
            smartStopElement.style.borderColor = this.model.smartStopColor.toRGBA(0.5);

            smartStopPlotLine?.setAttribute('title', this.model.smartStopTooltip);

            const lossPrice = '$' + Math.round(this.model.lossPrice) + '';
            if (stopPriceElement) {
                stopPriceElement.style.top = stopPriceTop + 'px';
                stopPriceElement.findFirst('p')?.ifExistThen((el: HTMLElement) => {
                    el.setInnerHtml(lossPrice);
                    el.style.color = this.model.smartStopColor.toRGB();
                });
            }
        }

        const smartTargetElement = this.element.findFirst('.smart-target');
        const targetPriceElement = this.element.findFirst('.target-price');
        const smartTargetPlotLine = this.base.tiChartSeries.element.findFirst('.highcharts-plot-line.smart-risk-target');
        let targetPriceTop = 0;
        if (smartTargetElement) {
            let top = 0;
            let shouldHideSmartTarget = !smartTargetPlotLine || !smartTargetPlotLine.getAttribute('d');
            if (this.model.smartTargetLevel < min) {
                top = yAxis.toPixels(min, true) + 30 + extraTop;
                shouldHideSmartTarget = true;
            } else if (this.model.smartTargetLevel > max) {
                top = yAxis.toPixels(max, true) + 30;
                shouldHideSmartTarget = true;
            } else {
                top = yAxis.toPixels(this.model.smartTargetLevel, true) + 30 + extraTop;
            }

            if (shouldHideSmartTarget) {
                smartTargetElement.addClass('hidden');
            } else {
                smartTargetElement.removeClass('hidden');
            }

            smartTargetElement.style.top = top + 'px';
            smartTargetElement.style.borderColor = this.model.smartTargetColor.toRGBA(0.5);
            smartTargetPlotLine?.setAttribute('title', this.model.smartTargetTooltip);

            const profitPrice = '$' + Math.round(this.model.profitPrice) + '';
            if (targetPriceElement) {
                targetPriceTop = this.model.isLong ? top + 16 : top;
                targetPriceElement.style.top = targetPriceTop + 'px';
                targetPriceElement.findFirst('p')?.ifExistThen((el: HTMLElement) => {
                    el.setInnerHtml(profitPrice);
                    el.style.color = this.model.smartTargetColor.toRGB();
                });
            }
        }

        let entryPriceToPixel = 0;
        if (this.model.entryPrice < min) {
            entryPriceToPixel = yAxis.toPixels(min, true);
        } else if (this.model.entryPrice > max) {
            entryPriceToPixel = yAxis.toPixels(max, true);
        } else {
            entryPriceToPixel = yAxis.toPixels(this.model.entryPrice, true);
        }

        let targetPriceToPixel = 0;
        if (this.model.targetPrice < min) {
            targetPriceToPixel = yAxis.toPixels(min, true);
        } else if (this.model.targetPrice > max) {
            targetPriceToPixel = yAxis.toPixels(max, true);
        } else {
            targetPriceToPixel = yAxis.toPixels(this.model.targetPrice, true);
        }

        let stopPriceToPixel = 0;
        if (this.model.stopPrice < min) {
            stopPriceToPixel = yAxis.toPixels(min, true);
        } else if (this.model.stopPrice > max) {
            stopPriceToPixel = yAxis.toPixels(max, true);
        } else {
            stopPriceToPixel = yAxis.toPixels(this.model.stopPrice, true);
        }

        const profitHeight = Math.abs(targetPriceToPixel - entryPriceToPixel);
        const lossHeight = Math.abs(stopPriceToPixel - entryPriceToPixel);

        this.gredientTotalHeight = 0;

        this.element.findFirst('.smart-risk-gredient')?.addClass('hidden');

        if (smartTargetPlotLine && smartStopPlotLine) {
            if (this.model.isLong) {
                this.element.findFirst('.smart-risk-gredient .profit-gredient')?.ifExistThen((el: HTMLElement) => {
                    el.style.height = profitHeight + 'px';
                    el.style.top = targetPriceTop + 'px';
                    this.gredientTotalHeight += profitHeight;

                    el.style.background = `linear-gradient(180deg, ${this.model.smartTargetColor.toRGBA(0.9)} 0%, rgba(255,255,255,0.1) 100%)`;
                });
                this.element.findFirst('.smart-risk-gredient .loss-gredient')?.ifExistThen((el: HTMLElement) => {
                    el.style.height = (stopPriceTop - (targetPriceTop + profitHeight) + 16) + 'px';
                    el.style.top = (targetPriceTop + profitHeight) + 'px';
                    this.gredientTotalHeight += lossHeight;

                    el.style.background = `linear-gradient(180deg, rgba(255,255,255,0.1) 0%, ${this.model.smartStopColor.toRGBA(0.9)} 100%)`;
                });
            } else {
                this.element.findFirst('.smart-risk-gredient .loss-gredient')?.ifExistThen((el: HTMLElement) => {
                    el.style.height = lossHeight + 'px';
                    el.style.top = stopPriceTop + 'px';
                    this.gredientTotalHeight += lossHeight;

                    el.style.background = `linear-gradient(180deg, ${this.model.smartStopColor.toRGBA(0.9)} 0%, rgba(255,255,255,0.1) 100%)`;
                });
                this.element.findFirst('.smart-risk-gredient .profit-gredient')?.ifExistThen((el: HTMLElement) => {
                    el.style.height = (targetPriceTop - (stopPriceTop + lossHeight) + 16) + 'px';

                    el.style.top = (stopPriceTop + lossHeight) + 'px';
                    this.gredientTotalHeight += profitHeight;

                    el.style.background = `linear-gradient(180deg, rgba(255,255,255,0.1) 0%, ${this.model.smartTargetColor.toRGBA(0.9)} 100%)`;
                });
            }

            this.shouldShowGredientColors = true;
        }
    }
}